"use client";
import { Input } from "@shared/ui.components";
import { Button, Drawer, Link, Typography } from "@shared/ui.primitives";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";

import {
  drawerBodyWrapper,
  errorInput,
  errorMessageStyle,
  forgottenPasswordLinkStyle,
  hidePasswordStyle,
  passwordSectionHeader
} from "./LoginFlyout.css";

/**
 * Form type for the login form used in LoginFlyout.
 */
export type LoginFormType = {
  loginEmail: string;
  loginPassword: string;
};

/**
 * Props for `LoginFlyout`.
 * @property isOpen - Whether the flyout drawer is open
 * @property handleDismiss - Callback for closing the flyout
 * @property methods - react-hook-form methods for the login form
 * @property onSubmitForm - Callback for form submission, receives login data
 * @property disableLoginForm - Whether to disable the login form/interactions
 * @property forgotPasswordRedirect - Redirect link when user clicks on `Forgotten your password?`
 * @property onJoinNowClick - Callback to redirect the user to `Join Now` page once clicking on `Join Now` CTA
 */
export type LoginFlyoutProps = {
  isOpen: boolean;
  handleDismiss: () => void;
  methods: UseFormReturn<LoginFormType>;
  onSubmitForm: (data: LoginFormType) => Promise<void>;
  disableLoginForm: boolean;
  forgotPasswordRedirect: string;
  onJoinNowClick: () => void;
};

/**
 * LoginFlyout component displays a sign-in drawer that allows users to
 * authenticate with email and password. Features include a password toggle,
 * forgotten password link, and a navigation button to join now.
 *
 * @component
 * @param props - LoginFlyoutProps
 * @returns A Drawer component with login form elements
 */
export const LoginFlyout: React.FC<LoginFlyoutProps> = ({
  isOpen,
  handleDismiss,
  methods,
  onSubmitForm,
  disableLoginForm,
  forgotPasswordRedirect,
  onJoinNowClick
}) => {
  const {
    register,
    formState: { errors, isSubmitting }
  } = methods;
  const [showPassword, setShowPassword] = useState(false);

  const signInDisabled = disableLoginForm || isSubmitting;
  const submitHandler = methods.handleSubmit(onSubmitForm);

  return (
    <Drawer
      id="login_flyout"
      headerId="login_flyout"
      isOpen={isOpen}
      anchorPosition="right"
    >
      <Drawer.Header
        id="login_flyout_header"
        handleDismiss={() => handleDismiss()}
      >
        Sign in to your account
      </Drawer.Header>
      <Drawer.Body>
        <div className={drawerBodyWrapper}>
          <div>
            <Typography variant="subheader-6" colour="primary-medium">
              Email
            </Typography>
            <Input
              id="email"
              type="email"
              placeholder="Email"
              aria-label="email"
              isDisabled={signInDisabled}
              isInvalid={!!errors.loginEmail}
              {...register("loginEmail", {
                required: "Please add your email address"
              })}
              className={errors.loginEmail ? errorInput : undefined}
            />
            {errors.loginEmail?.message && (
              <div className={errorMessageStyle}>
                <Typography
                  variant="smallprint-md"
                  colour="primary-light"
                  role="alert"
                  className={errorMessageStyle}
                >
                  {errors.loginEmail.message}
                </Typography>
              </div>
            )}
          </div>
          <div>
            <div className={passwordSectionHeader}>
              <Typography variant="subheader-6" colour="primary-medium">
                Password
              </Typography>
              <div
                className={hidePasswordStyle}
                aria-label={showPassword ? "hide_password" : "show_password"}
              >
                <Typography
                  variant="link-xs"
                  colour="primary-medium"
                  underline
                  onClick={() => setShowPassword(!showPassword)}
                  role="link"
                >
                  {showPassword ? "Hide" : "Show"}
                </Typography>
              </div>
            </div>
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              aria-label="password"
              isDisabled={signInDisabled}
              isInvalid={!!errors.loginPassword}
              {...register("loginPassword", {
                required: "Please enter your password"
              })}
              className={errors.loginPassword ? errorInput : undefined}
            />
            {errors.loginPassword?.message && (
              <div className={errorMessageStyle}>
                <Typography
                  variant="smallprint-md"
                  colour="primary-light"
                  role="alert"
                >
                  {errors.loginPassword.message}
                </Typography>
              </div>
            )}
            <div className={forgottenPasswordLinkStyle}>
              <Link
                href={forgotPasswordRedirect}
                variant="primary"
                text="Forgotten your password?"
                textAlign="right"
                textColour="primary-dark"
                size="small"
                noPadding
              />
            </div>
          </div>
        </div>
      </Drawer.Body>
      <Drawer.Footer>
        <Button
          variant="secondary"
          text="Join Now"
          width="full"
          role="link"
          onClick={onJoinNowClick}
          disabled={false}
        />
        <Button
          variant="primary"
          text="Sign In"
          width="full"
          type="button"
          onClick={() => {
            submitHandler();
          }}
          disabled={signInDisabled}
        />
      </Drawer.Footer>
    </Drawer>
  );
};
